import React, { useState, useEffect, useContext } from "react";
import {
  makeStyles,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import {
  ChevronRight as ChevronRightIcon,
  SettingsApplications as SettingsApplicationsIcon,
  PeopleAlt as PeopleAltIcon,
  WhatsApp as WhatsAppIcon,
  Store as StoreIcon,
  Tune as TuneIcon,
  Business as BusinessIcon,
  VpnKey as VpnKeyIcon,
  BarChart as BarChartIcon,
  AttachFile as AttachFileIcon,
  LocalAtm as LocalAtmIcon,
  Announcement as AnnouncementIcon,
  Code as CodeIcon,
} from "@material-ui/icons";

import { Link as RouterLink } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const MetaIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm5.273 6.773c-.275 0-.541.05-.8.133-.75-1.466-2.208-2.491-3.942-2.608v-.418h1.667V4.75H9.8v.842h1.667v.418c-1.733.117-3.192 1.142-3.942 2.608-.258-.083-.525-.133-.8-.133C4.633 8.485 3.5 9.6 3.5 11.108c0 1.467 1.133 2.567 2.667 2.567.333 0 .65-.058.95-.167C7.883 14.917 9.75 16 12 16s4.117-1.083 4.883-2.492c.3.11.617.167.95.167 1.534 0 2.667-1.1 2.667-2.567 0-1.467-1.133-2.567-2.667-2.567h.44zM8.233 13c-.858 0-1.566-.692-1.566-1.558 0-.866.708-1.559 1.566-1.559.859 0 1.567.693 1.567 1.559C9.8 12.308 9.092 13 8.233 13zm3.767.5c-.917 0-1.667-.75-1.667-1.667V11c0-.917.75-1.667 1.667-1.667S13.667 10.083 13.667 11v.833C13.667 12.75 12.917 13.5 12 13.5zm3.767-.5c-.859 0-1.567-.692-1.567-1.558 0-.866.708-1.559 1.567-1.559.858 0 1.566.693 1.566 1.559C17.333 12.308 16.625 13 15.767 13z"/>
  </svg>
);

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: theme.mode === "light" ? "#f8fafc" : "#0f172a",
    padding: "32px 24px 48px",
    maxWidth: 820,
    margin: "0 auto",
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: 26,
    color: theme.mode === "light" ? "#0f172a" : "#f1f5f9",
    letterSpacing: "-0.02em",
    marginBottom: 28,
  },

  // Info Card
  infoCard: {
    borderRadius: 14,
    border: `1px solid ${theme.mode === "light" ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)"}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)",
    background: theme.mode === "light" ? "#ffffff" : "#1e293b",
    padding: "18px 22px",
    marginBottom: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  infoLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  infoIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
    flexShrink: 0,
  },
  infoName: {
    fontWeight: 700,
    fontSize: 16,
    color: theme.mode === "light" ? "#0f172a" : "#f1f5f9",
    lineHeight: 1.3,
  },
  infoPlan: {
    fontSize: 13,
    color: theme.mode === "light" ? "#64748b" : "#94a3b8",
    marginTop: 2,
  },
  planBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    borderRadius: 20,
    background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.03em",
  },

  // Section label
  sectionLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.mode === "light" ? "#94a3b8" : "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 10,
    marginTop: 24,
    paddingLeft: 2,
  },

  // Rows container
  rowsCard: {
    borderRadius: 14,
    border: `1px solid ${theme.mode === "light" ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)"}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)",
    background: theme.mode === "light" ? "#ffffff" : "#1e293b",
    overflow: "hidden",
  },

  // Single row
  row: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "16px 20px",
    textDecoration: "none",
    color: "inherit",
    cursor: "pointer",
    transition: "background 0.15s ease",
    "&:hover": {
      background: theme.mode === "light" ? "#f8fafc" : "rgba(255,255,255,0.04)",
      "& $rowChevron": {
        transform: "translateX(3px)",
        color: "#2563eb",
      },
    },
  },
  rowIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": {
      fontSize: 20,
      color: "#fff",
    },
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
  },
  rowTitle: {
    fontWeight: 600,
    fontSize: 14,
    color: theme.mode === "light" ? "#0f172a" : "#f1f5f9",
    lineHeight: 1.3,
  },
  rowDesc: {
    fontSize: 12,
    color: theme.mode === "light" ? "#64748b" : "#94a3b8",
    marginTop: 2,
    lineHeight: 1.5,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowChevron: {
    color: theme.mode === "light" ? "#cbd5e1" : "#475569",
    fontSize: 20,
    transition: "transform 0.15s ease, color 0.15s ease",
    flexShrink: 0,
  },
  rowDivider: {
    margin: "0 20px",
    opacity: 0.5,
  },

  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    padding: 40,
  },
}));

const ROW_ITEMS = {
  integracoes: [
    {
      to: "/configuracoes/plataformas",
      icon: <StoreIcon />,
      iconBg: "linear-gradient(135deg, #f97316 0%, #ef4444 100%)",
      title: "Conectar Plataforma",
      desc: "Configure a plataforma de vendas do seu negócio (Hotmart, Kiwify, Eduzz...)",
    },
    {
      to: "/configuracoes/whatsapp",
      icon: <WhatsAppIcon />,
      iconBg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
      title: "Conexões WhatsApp",
      desc: "Adicione números, escaneie QR Code, reinicie e gerencie todas as conexões",
    },
    {
      to: "/messages-api",
      icon: <CodeIcon />,
      iconBg: "linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)",
      title: "API de Mensagens",
      desc: "Integre sistemas externos via API REST para envio de mensagens automáticas",
      adminOnly: false,
    },
    {
      to: "/configuracoes/meta-api",
      icon: <MetaIcon />,
      iconBg: "linear-gradient(135deg, #1877F2 0%, #0C63D4 100%)",
      title: "Integrações Meta",
      desc: "Configure a API Oficial do Meta para WhatsApp Business, Facebook e Instagram",
    },
  ],
  sistema: [
    {
      to: "/settings",
      icon: <TuneIcon />,
      iconBg: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      title: "Configurações do Sistema",
      desc: "Gerencie as opções de comportamento do sistema, setores e integrações",
    },
    {
      to: "/users",
      icon: <PeopleAltIcon />,
      iconBg: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
      title: "Usuários e Permissões",
      desc: "Adicione usuários, defina perfis e controle o acesso de cada membro",
    },
    {
      to: "/financeiro",
      icon: <LocalAtmIcon />,
      iconBg: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      title: "Financeiro",
      desc: "Visualize faturas, histórico de pagamentos e renove sua assinatura",
    },
    {
      to: "/files",
      icon: <AttachFileIcon />,
      iconBg: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      title: "Lista de Arquivos",
      desc: "Gerencie os arquivos de mídia usados nos fluxos e campanhas",
    },
  ],
  admin: [
    {
      to: "/announcements",
      icon: <AnnouncementIcon />,
      iconBg: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      title: "Informativos",
      desc: "Publique avisos e comunicados visíveis para todos os usuários da plataforma",
      superOnly: true,
    },
    {
      to: "/global-config",
      icon: <SettingsApplicationsIcon />,
      iconBg: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
      title: "Personalização da Plataforma",
      desc: "Mercado Pago, Asaas, e-mail, logos do login e configurações globais do SaaS",
      superOnly: true,
    },
    {
      to: "/server-metrics",
      icon: <BarChartIcon />,
      iconBg: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
      title: "Métricas do Servidor",
      desc: "Visualize o uso de CPU, memória, armazenamento e performance do servidor",
      superOnly: true,
    },
  ],
};

function SettingsRow({ item, last, user }) {
  const classes = useStyles();
  // superOnly items only visible to super users
  if (item.superOnly && !user?.super) return null;
  return (
    <>
      <RouterLink to={item.to} className={classes.row}>
        <Box className={classes.rowIcon} style={{ background: item.iconBg }}>
          {item.icon}
        </Box>
        <Box className={classes.rowContent}>
          <Typography className={classes.rowTitle}>{item.title}</Typography>
          <Typography className={classes.rowDesc}>{item.desc}</Typography>
        </Box>
        <ChevronRightIcon className={classes.rowChevron} />
      </RouterLink>
      {!last && <Divider className={classes.rowDivider} />}
    </>
  );
}

function SettingsSection({ label, items, user }) {
  const classes = useStyles();
  const visibleItems = items.filter(item => !item.superOnly || user?.super);
  if (visibleItems.length === 0) return null;
  return (
    <>
      <Typography className={classes.sectionLabel}>{label}</Typography>
      <Paper className={classes.rowsCard} elevation={0}>
        {visibleItems.map((item, i) => (
          <SettingsRow key={item.to} item={item} last={i === visibleItems.length - 1} user={user} />
        ))}
      </Paper>
    </>
  );
}

export default function ConfiguracoesHub() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [company, setCompany] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: co } = await api.get(`/companies/${user.companyId}`);
        setCompany(co);
        if (co.planId) {
          const { data: pl } = await api.get(`/plans/${co.planId}`);
          setPlan(pl);
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    if (user?.companyId) load();
  }, [user]);

  if (loading) {
    return (
      <Box className={classes.loadingWrap}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      <Typography className={classes.pageTitle}>Configurações</Typography>

      {/* Company info card */}
      <Paper className={classes.infoCard} elevation={0}>
        <Box className={classes.infoLeft}>
          <Box className={classes.infoIconWrap}>
            <BusinessIcon style={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography className={classes.infoName}>
              {company?.name || user?.name || "Minha empresa"}
            </Typography>
            <Typography className={classes.infoPlan}>
              {plan?.name ? `Plano ${plan.name}` : "Plano ativo"}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.planBadge}>
          {plan?.name || "Pro"}
        </Box>
      </Paper>

      {/* Integrações */}
      <SettingsSection label="Integrações" items={ROW_ITEMS.integracoes} user={user} />

      {/* Sistema */}
      <SettingsSection label="Sistema" items={ROW_ITEMS.sistema} user={user} />

      {/* Administração */}
      <SettingsSection label="Administração" items={ROW_ITEMS.admin} user={user} />
    </div>
  );
}
